import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { hasElectronPreload, isInTauriShell } from '@renderer/lib/runtime'

/**
 * Browser-only kiosk hardening behind the "Reduce browser gestures" setting
 * (settingsStore.suppressBrowserGestures). Native shells (Electron/Tauri) manage
 * their own chrome and never mount any of this.
 *
 * A touchscreen booth running as a home-screen web app (iPad + Chrome "Add to
 * Home Screen") exposes gestures a keyboard-driven booth never had to care about:
 * the left/right edge swipe navigates history, pinch zooms the page, and
 * pull-to-refresh reloads mid-session. `overscroll-behavior` (the original fix)
 * only covers the last one, so this component layers:
 *
 *  1. the `db-suppress-browser-gestures` root class (overscroll + touch-action CSS),
 *  2. an edge-zone touchstart guard — WebKit only arms the history swipe when the
 *     touch is allowed to begin at the screen edge, so cancelling it there kills
 *     the gesture at the source. Touches on interactive elements are left alone:
 *     a tap on a button near the edge is not a swipe,
 *  3. pinch suppression: WebKit's proprietary `gesturestart`/`gesturechange`
 *     (fires for pinch in Safari/standalone apps) plus a multi-touch touchmove
 *     fallback for non-WebKit engines,
 *  4. a viewport-meta lockdown (`maximum-scale=1, user-scalable=no`) — honored by
 *     home-screen web apps even where in-browser Safari ignores it,
 *  5. a history pin: the current entry is duplicated, and any `popstate` that
 *     still gets through (mouse back button, keyboard, a swipe that started on a
 *     button) immediately re-pins — the URL never actually changes, so the router
 *     stays on the page it was on.
 */

/** Touches starting within this many px of the left/right edge are treated as history-swipe attempts. */
const EDGE_SWIPE_GUARD_PX = 32

/** Elements whose touches are never cancelled — a tap is not a swipe. */
const INTERACTIVE_SELECTOR =
  'button, a, input, select, textarea, [role="button"], [contenteditable="true"]'

const isGuardActive = (enabled: boolean): boolean =>
  enabled && !hasElectronPreload() && !isInTauriShell()

/**
 * True while a full pin escape is being walked back with history.forward().
 * Module-level on purpose: the escape re-renders the router on the WRONG page,
 * which re-runs the pin effect there — and arming (pushState) on that page
 * would truncate the forward history the recovery is about to traverse. The
 * new effect instance checks this latch and holds off instead.
 */
let recoveryPending = false

export function KioskGestureGuard(): null {
  const suppressBrowserGestures = useSettingsStore((s) => s.suppressBrowserGestures)
  const { pathname } = useLocation()
  const active = isGuardActive(suppressBrowserGestures)

  // Root class + touch listeners + viewport meta.
  useEffect(() => {
    const root = document.documentElement
    if (!active) {
      root.classList.remove('db-suppress-browser-gestures')
      return
    }
    root.classList.add('db-suppress-browser-gestures')

    const onTouchStart = (e: TouchEvent): void => {
      // A second finger landing = pinch forming. Cancelling the new touch here
      // (not the whole gesture) is what actually stops Safari from zooming.
      if (e.touches.length > 1) {
        e.preventDefault()
        return
      }
      const touch = e.touches[0]
      if (!touch) return
      const nearEdge =
        touch.clientX <= EDGE_SWIPE_GUARD_PX ||
        touch.clientX >= window.innerWidth - EDGE_SWIPE_GUARD_PX
      if (!nearEdge) return
      const target = e.target as Element | null
      if (target?.closest?.(INTERACTIVE_SELECTOR)) return
      e.preventDefault()
    }

    const onTouchMove = (e: TouchEvent): void => {
      if (e.touches.length > 1) e.preventDefault()
    }

    const preventGesture = (e: Event): void => {
      e.preventDefault()
    }

    // capture + non-passive: preventDefault must reach WebKit before it arms
    // the native gesture, and passive listeners are not allowed to cancel.
    const opts: AddEventListenerOptions = { passive: false, capture: true }
    document.addEventListener('touchstart', onTouchStart, opts)
    document.addEventListener('touchmove', onTouchMove, opts)
    // WebKit-proprietary pinch/rotate events; no-ops elsewhere.
    document.addEventListener('gesturestart', preventGesture, opts)
    document.addEventListener('gesturechange', preventGesture, opts)

    const viewportMeta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]')
    const originalViewport = viewportMeta?.getAttribute('content') ?? null
    if (viewportMeta && originalViewport && !originalViewport.includes('user-scalable')) {
      viewportMeta.setAttribute(
        'content',
        `${originalViewport}, maximum-scale=1.0, user-scalable=no`
      )
    }

    return (): void => {
      root.classList.remove('db-suppress-browser-gestures')
      document.removeEventListener('touchstart', onTouchStart, opts)
      document.removeEventListener('touchmove', onTouchMove, opts)
      document.removeEventListener('gesturestart', preventGesture, opts)
      document.removeEventListener('gesturechange', preventGesture, opts)
      if (viewportMeta && originalViewport) {
        viewportMeta.setAttribute('content', originalViewport)
      }
    }
  }, [active])

  // History pin — self-healing, depth 2, tagged.
  //
  // On iOS the edge swipe is a SYSTEM gesture that page JS cannot cancel, so
  // the only defense is making the entry behind us a duplicate of the current
  // page. The first version kept exactly one duplicate and re-pushed it inside
  // `popstate` — which loses two races the field promptly found:
  //   1. two pops in quick succession (a fast double swipe) consume the pin and
  //      then a REAL entry before the re-push registers, and
  //   2. WebKit can silently drop a pushState issued while the swipe's history
  //      transaction is still settling — after which a single further swipe
  //      escapes cleanly.
  // Both reproduce on desktop with `history.back(); history.back()`.
  //
  // So: keep a buffer of PIN_DEPTH duplicates, each tagged in its state with
  // `__dbPin: n` (React Router keeps its own fields — `usr`, `key`, `idx` —
  // and ignores extras, so the location it derives is identical: no remount).
  // The tag makes recovery exact: whatever entry a pop lands on says how many
  // pins survive, and the handler pushes back only the missing ones — no
  // guessing, no unbounded growth. A deferred re-check after the gesture
  // transaction closes catches the dropped-push case, and a pop that somehow
  // lands on a DIFFERENT page walks forward again.
  useEffect(() => {
    if (!active) return

    const PIN_DEPTH = 2
    /** Delay for the post-gesture verification — WebKit's swipe transaction has closed by then. */
    const VERIFY_MS = 150
    const armedPath = window.location.pathname
    let verifyTimer: ReturnType<typeof setTimeout> | null = null
    let disposed = false

    const pinLevel = (): number => {
      const state = window.history.state as { __dbPin?: number } | null
      return typeof state?.__dbPin === 'number' ? state.__dbPin : 0
    }

    const pushPin = (level: number): void => {
      try {
        const base =
          window.history.state && typeof window.history.state === 'object'
            ? (window.history.state as Record<string, unknown>)
            : {}
        window.history.pushState({ ...base, __dbPin: level }, '', window.location.href)
      } catch {
        // pushState can throw when rate-limited (~100 calls/30s in Safari); the
        // guard degrades to the touch layer alone rather than breaking the page.
      }
    }

    const topUp = (): void => {
      for (let level = pinLevel() + 1; level <= PIN_DEPTH; level++) {
        pushPin(level)
      }
    }

    const scheduleVerify = (): void => {
      if (verifyTimer) clearTimeout(verifyTimer)
      verifyTimer = setTimeout(() => {
        verifyTimer = null
        if (!disposed && pinLevel() < PIN_DEPTH) topUp()
      }, VERIFY_MS)
    }

    const onPopState = (): void => {
      // A pop that reached a different page means every pin was consumed —
      // return to where we were. The escaped-from entry is now forward
      // history; the latch keeps the effect that mounts on the wrong page
      // from pushing (and truncating that forward history) in the meantime.
      if (window.location.pathname !== armedPath) {
        recoveryPending = true
        try {
          window.history.forward()
        } catch {
          recoveryPending = false
        }
        return
      }
      recoveryPending = false
      topUp()
      scheduleVerify()
    }

    if (recoveryPending) {
      // Mounted mid-recovery (on the escaped-to page). Don't arm here — wait
      // for the forward() traversal; if it never lands (entry gone), release
      // the latch and pin this page after all.
      verifyTimer = setTimeout(() => {
        verifyTimer = null
        if (!disposed && recoveryPending) {
          recoveryPending = false
          topUp()
        }
      }, 400)
    } else {
      topUp()
    }
    window.addEventListener('popstate', onPopState)
    return (): void => {
      disposed = true
      if (verifyTimer) clearTimeout(verifyTimer)
      window.removeEventListener('popstate', onPopState)
    }
  }, [active, pathname])

  return null
}

export default KioskGestureGuard
